/**
 * Signed addenda for a locked Progress Note, oldest first, plus the open draft
 * if there is one. Renders below the note so the original reads first.
 */
import { Badge, Group, Paper, Stack, Text } from '@mantine/core'
import { useResource } from '@medplum/react'
import { IconLock, IconPencil } from '@tabler/icons-react'
import { NoteHeading } from '../ProgressNoteEdit/ProgressNoteSections/NoteHeading'
import { ADDENDUM_REASON_LABELS, type NoteAddendum } from '@/pages/Patient/hooks/useNoteAddenda'
import { formatDateTime, formatSignatureLine } from '@/utils'
import { getPractitionerDisplayName } from '@/utils/providerUtils'

const SIGNED = { Icon: IconLock, color: '#1e3a5f', label: 'Signed electronically' }
const DRAFT = { Icon: IconPencil, color: '#6b7280', label: 'Draft — not yet part of the record' }

function AddendumCard({ addendum }: { addendum: NoteAddendum }) {
  const status = addendum.signed ? SIGNED : DRAFT
  const signer = useResource(addendum.signer)
  const signerName = signer ? getPractitionerDisplayName(signer) : addendum.author

  return (
    <Paper
      p="sm"
      withBorder
      data-testid={`addendum-${addendum.id}`}
      style={{ borderLeft: `3px solid ${addendum.signed ? '#1e3a5f' : '#d1d5db'}` }}
    >
      <Group justify="space-between" wrap="wrap" gap="xs" mb={6}>
        <Group gap="xs" wrap="wrap">
          <Badge variant="light" color={addendum.signed ? 'blue' : 'gray'} size="sm">
            {ADDENDUM_REASON_LABELS[addendum.reason]}
          </Badge>
          <Group gap={4} wrap="nowrap">
            <status.Icon size={12} color={status.color} />
            <Text size="xs" fw={600} c={status.color}>
              {status.label}
            </Text>
          </Group>
        </Group>
        <Text size="xs" c="dimmed">
          {addendum.signed && addendum.date
            ? formatSignatureLine(signerName, addendum.date)
            : `${addendum.author}${addendum.date ? ` · ${formatDateTime(addendum.date, '-', true)}` : ''}`}
        </Text>
      </Group>
      <Text size="sm" c="dark.7" style={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
        {addendum.content}
      </Text>
    </Paper>
  )
}

export type AddendaSectionProps = {
  signedAddenda: NoteAddendum[]
  draft?: NoteAddendum
}

export function AddendaSection({ signedAddenda, draft }: AddendaSectionProps) {
  if (signedAddenda.length === 0 && !draft) return null

  return (
    <Paper p="md" withBorder data-testid="progress-note-addenda">
      <NoteHeading level="section" id="addenda" label="Addendum" />
      <Stack gap="sm">
        {signedAddenda.map((addendum) => (
          <AddendumCard key={addendum.id} addendum={addendum} />
        ))}
        {draft && <AddendumCard addendum={draft} />}
      </Stack>
    </Paper>
  )
}
